import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInvite } from '@/api';
import type { InviteData } from '@/api';

export default function InvitePage() {
  const navigate = useNavigate();
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  useEffect(() => {
    let mounted = true;
    getInvite()
      .then((res) => {
        if (mounted) setInviteData(res);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : '加载失败');
      });
    return () => {
      mounted = false;
    };
  }, []);

  //注册页地址（携带邀请码）：origin + 部署子目录（如 /web/）自适应
  const baseUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : '';
  const inviteLink = inviteData ? `${baseUrl}#/register?invite_code=${inviteData.inviteCode}` : '';
  const qrSrc = inviteLink
    ? `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(inviteLink)}`
    : '';

  const handleCopyCode = () => {
    if (!inviteData) return;
    navigator.clipboard.writeText(inviteData.inviteCode).then(() => {
      showToast('邀请码已复制');
    }).catch(() => {
      showToast('复制成功');
    });
  };

  const handleCopyLink = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink).then(() => {
      showToast('邀请链接已复制');
    }).catch(() => {
      showToast('复制成功');
    });
  };

  return (
    <div className="mobile-container pb-10">
      {/* Toast */}
      {toast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 bg-foreground-800 text-white text-sm rounded-full shadow-lg animate-bounce">
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-green-400"></i>
            <span>{toast}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-sm border-b border-background-100">
        <div className="flex items-center gap-3 px-4 py-3.5">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl text-foreground-700"></i>
          </button>
          <h1 className="text-base font-semibold text-foreground-900">邀请好友</h1>
        </div>
      </header>

      <div className="px-4 space-y-4 pt-4">
        {/* Brand Card */}
        <div className="relative rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-primary-400 to-red-400"></div>
          {/* Decorative circles */}
          <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/10"></div>
          <div className="absolute -bottom-8 right-4 w-20 h-20 rounded-full bg-white/8"></div>
          <div className="absolute top-4 right-12 w-10 h-10 rounded-full bg-white/12"></div>

          <div className="relative px-5 py-6 flex flex-col items-center">
            {/* Invite badge */}
            <div className="relative w-14 h-14 mb-3">
              <div className="absolute inset-0 bg-primary-400/25 rounded-2xl blur-xl scale-125" />
              <div className="relative w-14 h-14 rounded-2xl bg-primary-500 flex items-center justify-center">
                <span className="text-white text-2xl font-bold tracking-wider">众</span>
              </div>
            </div>
            <h2 className="text-white text-lg font-bold mb-1">{inviteData?.platformName || '众泰成商城'}</h2>
            <p className="text-white/70 text-xs tracking-widest">{inviteData?.slogan || ''}</p>
          </div>
        </div>

        {/* Invite Card — white card with QR and code */}
        <div className="bg-white rounded-2xl border border-background-100 px-5 py-5">
          {/* Invite info */}
          <div className="text-center mb-4">
            <p className="text-foreground-500 text-sm">{inviteData?.inviteTitle || '邀请您加入'}</p>
            <p className="text-foreground-700 text-sm font-medium mt-1">{inviteData?.inviteDesc || ''}</p>
          </div>

          {/* QR Code area */}
          <div className="flex justify-center mb-5">
            <div className="bg-foreground-50 rounded-xl p-3 border border-background-200">
              <div className="w-40 h-40 bg-foreground-900 rounded-lg flex items-center justify-center overflow-hidden">
                {qrSrc ? (
                  <img
                    src={qrSrc}
                    alt="邀请二维码"
                    className="w-36 h-36 object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center">
                    <i className="ri-loader-4-line text-white/60 text-3xl animate-spin mb-2" />
                    <p className="text-white/40 text-xs">{error || '二维码生成中'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Invite code */}
          <div className="text-center">
            <p className="text-foreground-400 text-xs mb-1.5">专属邀请码</p>
            <div className="flex items-center justify-center gap-2">
              <span className="text-primary-500 text-2xl font-bold tracking-widest font-mono">
                {inviteData?.inviteCode || '------'}
              </span>
            </div>
          </div>
        </div>

        {/* Rules Card */}
        <div className="bg-amber-50 border border-amber-200/70 rounded-2xl px-4 py-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-question-line text-amber-500 text-base"></i>
            </div>
            <span className="text-amber-700 text-xs font-semibold">邀请说明</span>
          </div>
          <ul className="space-y-2">
            {(inviteData?.rules || []).map((rule, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-amber-200 flex items-center justify-center mt-0.5">
                  <span className="text-amber-600 text-[10px] font-bold">{idx + 1}</span>
                </span>
                <span className="text-amber-700/90 text-xs leading-relaxed">{rule}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleCopyCode}
            disabled={!inviteData}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl bg-white border border-background-200 text-foreground-700 text-sm font-medium hover:bg-background-50 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-60"
          >
            <i className="ri-file-copy-line text-foreground-400"></i>
            <span>复制邀请码</span>
          </button>
          <button
            onClick={handleCopyLink}
            disabled={!inviteData}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 active:scale-[0.98] transition-all shadow-sm shadow-primary-500/20 cursor-pointer disabled:opacity-60"
          >
            <i className="ri-link text-white/80"></i>
            <span>复制邀请链接</span>
          </button>
        </div>

        {/* Share hint */}
        <div className="text-center py-2">
          <p className="text-foreground-400 text-xs">
            也可以截图分享本页面给好友
          </p>
        </div>
      </div>
    </div>
  );
}
